import _extends from '@babel/runtime/helpers/extends';
import * as React from 'react';
import { useId } from 'react';
import { StyleSheet } from 'aphrodite';
import { addStyle, View } from '@khanacademy/wonder-blocks-core';
import { createThemeContext, ThemeSwitcherContext, useScopedTheme, useStyles } from '@khanacademy/wonder-blocks-theming';
import { focusStyles } from '@khanacademy/wonder-blocks-styles';
import { semanticColor, color, border, spacing } from '@khanacademy/wonder-blocks-tokens';

const action = semanticColor.action.secondary.progressive;
const theme = {
  color: {
    bg: {
      switch: {
        off: action.default.border,
        disabledOff: semanticColor.action.primary.disabled.background,
        activeOff: color.fadedOffBlack64,
        on: action.default.foreground,
        disabledOn: action.press.background,
        activeOn: action.press.foreground
      },
      slider: {
        on: semanticColor.icon.inverse,
        off: semanticColor.icon.inverse
      },
      icon: {
        on: action.default.foreground,
        disabledOn: action.press.background,
        off: action.default.border,
        disabledOff: semanticColor.icon.disabled
      }
    }
  },
  border: {
    radius: {
      small: border.radius.radius_120,
      full: border.radius.radius_full
    }
  },
  size: {
    height: {
      none: 0,
      medium: 20,
      large: spacing.large_24
    },
    width: {
      none: 0,
      small: spacing.xxxxSmall_2,
      medium: 20,
      large: 40
    }
  },
  spacing: {
    slider: {
      position: spacing.xxxxSmall_2
    },
    icon: {
      position: spacing.xxxSmall_4
    },
    transform: {
      default: `translateX(${spacing.medium_16}px)`,
      transition: "transform 0.15s ease-in-out"
    }
  }
};

const themes = {
  default: theme
};
const SwitchThemeContext = createThemeContext(theme);
function ThemedSwitch(props) {
  const currentTheme = React.useContext(ThemeSwitcherContext);
  const theme$1 = themes[currentTheme] || theme;
  return React.createElement(SwitchThemeContext.Provider, {
    value: theme$1
  }, props.children);
}

const StyledSpan = addStyle("span");
const StyledInput = addStyle("input");
const SwitchCore = React.forwardRef(function SwitchCore(props, ref) {
  const {
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledBy,
    "aria-describedby": ariaDescribedBy,
    checked,
    className,
    disabled = false,
    icon,
    id,
    onChange,
    testId
  } = props;
  const generatedUniqueId = useId();
  const uniqueId = id != null ? id : generatedUniqueId;
  const {
    theme,
    themeName
  } = useScopedTheme(SwitchThemeContext);
  const sharedStyles = useStyles(themedSharedStyles, theme);
  const handleClick = () => {
    if (!disabled && onChange) {
      onChange(!checked);
    }
  };
  const handleChange = () => {};
  const stateStyles = _generateStyles(checked, onChange !== undefined, disabled, theme, themeName);
  let styledIcon;
  if (icon) {
    styledIcon = React.cloneElement(icon, {
      size: "small",
      style: [sharedStyles.icon, stateStyles.icon],
      "aria-hidden": true
    });
  }
  return React.createElement(View, {
    onClick: handleClick,
    style: [sharedStyles.switch, stateStyles.switch, disabled && sharedStyles.disabled],
    className: className,
    testId: testId
  }, React.createElement(StyledInput, {
    "aria-describedby": ariaDescribedBy,
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledBy,
    checked: checked,
    "aria-disabled": disabled,
    id: uniqueId,
    onChange: handleChange,
    ref: ref,
    role: "switch",
    style: sharedStyles.hidden,
    type: "checkbox"
  }), icon && styledIcon, React.createElement(StyledSpan, {
    style: [sharedStyles.slider, stateStyles.slider]
  }));
});
const themedSharedStyles = theme => ({
  hidden: {
    opacity: 0,
    height: theme.size.height.none,
    width: theme.size.width.none
  },
  switch: {
    display: "inline-flex",
    height: theme.size.height.large,
    width: theme.size.width.large,
    borderRadius: theme.border.radius.small,
    flexShrink: 0,
    ":focus-within": focusStyles.focus[":focus-visible"]
  },
  disabled: {
    cursor: "not-allowed",
    ":hover": {
      outline: "none"
    },
    ":active": {
      outline: "none"
    },
    ":focus-within": focusStyles.focus[":focus-visible"]
  },
  slider: {
    position: "absolute",
    top: theme.spacing.slider.position,
    left: theme.spacing.slider.position,
    height: theme.size.height.medium,
    width: theme.size.width.medium,
    borderRadius: theme.border.radius.full,
    backgroundColor: theme.color.bg.slider.on,
    transition: theme.spacing.transform.transition
  },
  icon: {
    position: "absolute",
    top: theme.spacing.icon.position,
    left: theme.spacing.icon.position,
    zIndex: 1,
    transition: theme.spacing.transform.transition
  }
});
const focusStylesObject = focusStyles.focus[":focus-visible"];
const styles = {};
const _generateStyles = (checked, clickable, disabled, theme, themeName) => {
  const checkedStyle = `${checked}-${clickable}-${disabled}-${themeName}`;
  if (styles[checkedStyle]) {
    return styles[checkedStyle];
  }
  let newStyles = {};
  const sharedSwitchStyles = {
    cursor: clickable ? "pointer" : "auto",
    ":hover": _extends({}, focusStylesObject, {
      outline: clickable ? focusStylesObject.outline : "none"
    })
  };
  if (checked) {
    newStyles = {
      switch: _extends({
        backgroundColor: disabled ? theme.color.bg.switch.disabledOn : theme.color.bg.switch.on,
        ":active": _extends({
          backgroundColor: !disabled && clickable ? theme.color.bg.switch.activeOn : undefined
        }, focusStylesObject, {
          outline: clickable ? focusStylesObject.outline : "none"
        })
      }, sharedSwitchStyles),
      slider: {
        transform: theme.spacing.transform.default
      },
      icon: {
        color: disabled ? theme.color.bg.icon.disabledOn : theme.color.bg.icon.on,
        transform: theme.spacing.transform.default
      }
    };
  } else {
    newStyles = {
      switch: _extends({
        backgroundColor: disabled ? theme.color.bg.switch.disabledOff : theme.color.bg.switch.off,
        ":active": _extends({
          backgroundColor: !disabled && clickable ? theme.color.bg.switch.activeOff : undefined
        }, focusStylesObject, {
          outline: clickable ? focusStylesObject.outline : "none"
        })
      }, sharedSwitchStyles),
      slider: {
        backgroundColor: theme.color.bg.slider.off
      },
      icon: {
        color: disabled ? theme.color.bg.icon.disabledOff : theme.color.bg.icon.off
      }
    };
  }
  styles[checkedStyle] = StyleSheet.create(newStyles);
  return styles[checkedStyle];
};
const Switch = React.forwardRef(function Switch(props, ref) {
  return React.createElement(ThemedSwitch, null, React.createElement(SwitchCore, _extends({}, props, {
    ref: ref
  })));
});
Switch.displayName = "Switch";

export { Switch as default };
